import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowLeft, Trash2, ListOrdered, XCircle, CalendarCheck } from 'lucide-react';
import apiService from '../services/api';
import type { Appointment, AppointmentSeriesSummary } from '../types';

interface CancellationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancellationData: { noShow: boolean; cancellationNotes: string; sendCancellationEmail: boolean; cancelScope?: 'THIS' | 'REMAINING' }) => void;
  appointmentDetails: string;
  appointment?: Appointment | any;
  isLoading?: boolean;
}

const CancellationDialog: React.FC<CancellationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  appointmentDetails,
  appointment,
  isLoading = false
}) => {
  const [noShow, setNoShow] = useState(false);
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const [cancelScope, setCancelScope] = useState<'THIS' | 'REMAINING'>('THIS');
  const [seriesSummary, setSeriesSummary] = useState<AppointmentSeriesSummary | null>(null);
  const [isSeriesLoading, setIsSeriesLoading] = useState(false);

  const recurringMeta = useMemo(() => {
    const seriesId = appointment?.seriesId ?? appointment?.series_id ?? null;
    const occurrenceIndexRaw = appointment?.occurrenceIndex ?? appointment?.occurrence_index ?? null;
    const seriesOccurrenceCountRaw = appointment?.seriesOccurrenceCount ?? appointment?.series_occurrence_count ?? null;

    const occurrenceIndex = occurrenceIndexRaw != null ? Number(occurrenceIndexRaw) : null;
    const seriesOccurrenceCount = seriesOccurrenceCountRaw != null ? Number(seriesOccurrenceCountRaw) : null;

    return {
      seriesId: seriesId ? String(seriesId) : null,
      occurrenceIndex: Number.isFinite(occurrenceIndex as any) ? (occurrenceIndex as number) : null,
      seriesOccurrenceCount: Number.isFinite(seriesOccurrenceCount as any) ? (seriesOccurrenceCount as number) : null
    };
  }, [appointment]);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen) return;

    setCancelScope('THIS');
    setSeriesSummary(null);

    const appointmentId = appointment?.id;
    if (!appointmentId) return;

    setIsSeriesLoading(true);
    apiService
      .getAppointmentSeriesSummary(String(appointmentId))
      .then((data) => {
        if (cancelled) return;
        setSeriesSummary(data);
      })
      .catch(() => {
        if (cancelled) return;
        setSeriesSummary(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsSeriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, appointment?.id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({
      noShow,
      cancellationNotes,
      sendCancellationEmail,
      cancelScope: (seriesSummary?.isRecurring === true || recurringMeta.seriesId) ? cancelScope : 'THIS'
    });
  };

  const handleClose = () => {
    setNoShow(false);
    setCancellationNotes('');
    setSendCancellationEmail(false);
    setCancelScope('THIS');
    setSeriesSummary(null);
    onClose();
  };

  if (!isOpen) return null;

  const remainingAfterThisFallback =
    recurringMeta.occurrenceIndex && recurringMeta.seriesOccurrenceCount
      ? Math.max(recurringMeta.seriesOccurrenceCount - recurringMeta.occurrenceIndex, 0)
      : 0;

  const remainingActiveAfterThis =
    typeof seriesSummary?.remainingActiveAfterThis === 'number'
      ? seriesSummary.remainingActiveAfterThis
      : remainingAfterThisFallback;

  const isRecurring = seriesSummary?.isRecurring === true || Boolean(recurringMeta.seriesId);

  const showCancelScopeChoice =
    isRecurring &&
    Number.isFinite(remainingActiveAfterThis) &&
    remainingActiveAfterThis > 0;

  const completedLabel = (() => {
    if (typeof seriesSummary?.completedCount === 'number') return seriesSummary.completedCount;
    if (recurringMeta.occurrenceIndex && recurringMeta.occurrenceIndex > 0) return Math.max(recurringMeta.occurrenceIndex - 1, 0);
    return null;
  })();

  const remainingLabel = (() => {
    if (typeof seriesSummary?.remainingActiveIncludingThis === 'number') return seriesSummary.remainingActiveIncludingThis;
    if (recurringMeta.occurrenceIndex && recurringMeta.seriesOccurrenceCount) {
      return Math.max(recurringMeta.seriesOccurrenceCount - recurringMeta.occurrenceIndex + 1, 1);
    }
    return null;
  })();

  const seriesOccurrenceCountForDisplay =
    (typeof (seriesSummary as any)?.seriesOccurrenceCount === 'number' ? (seriesSummary as any).seriesOccurrenceCount : null) ??
    (typeof (seriesSummary as any)?.series_occurrence_count === 'number' ? (seriesSummary as any).series_occurrence_count : null) ??
    recurringMeta.seriesOccurrenceCount;

  const occurrenceIndexForDisplay =
    (typeof (seriesSummary as any)?.occurrenceIndex === 'number' ? (seriesSummary as any).occurrenceIndex : null) ??
    (typeof (seriesSummary as any)?.occurrence_index === 'number' ? (seriesSummary as any).occurrence_index : null) ??
    recurringMeta.occurrenceIndex;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4 z-[2000]" style={{ zIndex: 2000 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-0 border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <span>Cancel Appointment</span>
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this appointment?
            </p>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-900">{appointmentDetails}</p>
            </div>
            {/* Recurring series info + cancel scope (only when applicable) */}
            {isRecurring && (
              <div className="mt-4 border border-blue-100 bg-blue-50/40 rounded-md p-4">
                <p className="text-sm font-semibold text-gray-900">This appointment is part of a series</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {occurrenceIndexForDisplay && seriesOccurrenceCountForDisplay ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-200 bg-white text-xs font-semibold text-blue-900">
                      <ListOrdered className="w-3.5 h-3.5 text-blue-700" aria-hidden />
                      <span>
                        {occurrenceIndexForDisplay} of {seriesOccurrenceCountForDisplay}
                      </span>
                    </span>
                  ) : null}
                  {completedLabel != null ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                      <span>Completed: {completedLabel}</span>
                    </span>
                  ) : null}
                  {remainingLabel != null ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                      <span>Remaining: {remainingLabel}</span>
                    </span>
                  ) : null}
                </div>

                {isSeriesLoading ? (
                  <p className="mt-2 text-xs text-gray-600">Checking remaining appointments in this series…</p>
                ) : null}

                {showCancelScopeChoice ? (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">What would you like to cancel?</p>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="cancelScope"
                          value="THIS"
                          checked={cancelScope === 'THIS'}
                          onChange={() => setCancelScope('THIS')}
                          disabled={isLoading}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Cancel only this appointment</p>
                          <p className="text-xs text-gray-600">All other upcoming appointments in the series will stay scheduled.</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-white hover:bg-red-50 cursor-pointer">
                        <input
                          type="radio"
                          name="cancelScope"
                          value="REMAINING"
                          checked={cancelScope === 'REMAINING'}
                          onChange={() => setCancelScope('REMAINING')}
                          disabled={isLoading}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Cancel this and all remaining appointments</p>
                          <p className="text-xs text-gray-600">
                            This will also cancel {remainingActiveAfterThis} upcoming appointment{remainingActiveAfterThis === 1 ? '' : 's'} in this series.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

          {/* Send cancellation email to customer */}
          <div className="mt-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendCancellationEmail}
                onChange={(e) => setSendCancellationEmail(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={isLoading}
              />
              <span className="text-sm font-medium text-gray-900">Send cancellation email to customer</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Check this to notify the customer by email; leave unchecked to cancel without sending an email.
            </p>
          </div>

          {/* Cancellation Reason */}
          <div className="mt-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={noShow}
                onChange={(e) => setNoShow(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={isLoading}
              />
              <span className="text-sm font-medium text-gray-900">No Show</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Check this if the customer didn't show up for the appointment
            </p>
          </div>

          {/* Cancellation Notes */}
          <div className="mt-4">
            <label htmlFor="cancellationNotes" className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Notes (Optional)
            </label>
            <textarea
              id="cancellationNotes"
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Enter any additional notes about the cancellation..."
              disabled={isLoading}
            />
          </div>
          </div>

          {/* Actions */}
          <div className="flex w-full justify-stretch sm:justify-end gap-3 pt-4 border-t border-gray-200 shrink-0 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex flex-1 sm:flex-none justify-center items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              disabled={isLoading}
            >
              <CalendarCheck className="w-4 h-4 mr-2" aria-hidden />
              <span className="sm:hidden">Keep</span>
              <span className="hidden sm:inline">Keep Appointment</span>
            </button>
            <button
              type="submit"
              className="inline-flex flex-1 sm:flex-none justify-center items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Cancelling...</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" aria-hidden />
                  <span className="sm:hidden">I'd like to Cancel</span>
                  <span className="hidden sm:inline">Cancel Appointment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancellationDialog; 